import { useEffect } from 'react';

import { isLocal } from '../utils';

/**
 * Set right before a deliberate full-page navigation we chose ourselves — the
 * SSO provider redirect — so the "leave site?" prompt below does not fire on it.
 *
 * Module-level rather than component state: the listener is attached once, high
 * in the tree (`src/app/page.tsx`), while the code that knows a redirect is
 * intentional (`StudentAuthGate`) lives in a completely different subtree.
 * Passing this through props/context would mean threading it across components
 * that have no other reason to know about each other.
 *
 * One-shot by design — consumed by the very next `beforeunload` event, then reset
 * to false. Two reasons this is deliberately narrow rather than a lasting
 * on/off switch:
 *   - If `signIn()` actually navigates, the whole JS context is destroyed by the
 *     browser navigation anyway, so nothing needs to reset it.
 *   - If it does NOT navigate (e.g. the CSRF fetch inside signIn() fails before
 *     the redirect), the flag must not silently disable the warning for the
 *     rest of the session.
 */
let allowNextNavigation = false;

export const allowNavigationAway = (): void => {
  allowNextNavigation = true;
};

const useBeforeUnload = () => {
  useEffect(() => {
    const fnRef = function (e: BeforeUnloadEvent) {
      if (allowNextNavigation) {
        allowNextNavigation = false;
        return;
      }
      // Cancel the event
      e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      // Chrome requires returnValue to be set
      e.returnValue = '';

      // Safari doesnt catch this but you can navigate back and forth
    };
    if (!isLocal()) {
      window.addEventListener('beforeunload', fnRef);
    }
    return () => {
      if (!isLocal()) {
        window.removeEventListener('beforeunload', fnRef);
      }
    };
  }, []);

  return null;
};
export default useBeforeUnload;
